import { useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ArticleHero from './components/ArticleHero';
import ArticleContent from './components/ArticleContent';
import ArticleCTA from './components/ArticleCTA';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.mom';

function buildArticleJsonLd(article: (typeof articles)[0]) {
  const articleUrl = `${siteUrl}/article/${article.slug}`;
  const schemas: Record<string, unknown>[] = [];

  schemas.push({
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: articleUrl,
    author: {
      '@type': 'Organization',
      name: 'LitBuy Spreadsheets Editorial Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'LitBuy Spreadsheets',
      url: siteUrl,
    },
    datePublished: '2026-06-01',
    dateModified: '2026-06-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'en',
    isAccessibleForFree: true,
  });

  schemas.push({
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: articleUrl,
      },
    ],
  });

  if (article.faqs.length > 0) {
    schemas.push({
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    });
  }

  return schemas;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={`${article.title} | LitBuy Spreadsheets`}
        description={article.excerpt}
        keywords={`${article.title.toLowerCase()}, litbuy spreadsheet, litbuy spreadsheet guide, litbuy spreadsheet article, spreadsheet tutorial`}
        canonicalUrl={articleUrl}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={articleUrl}
        jsonLd={buildArticleJsonLd(article)}
      />
      <Navbar />
      <main>
        <ArticleHero />
        <ArticleContent article={article} />
        <ArticleCTA />
      </main>
      <Footer />
    </div>
  );
}